import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loader2, Clapperboard, Plus, Play, Eye, Star } from "lucide-react";
import { toast } from "sonner";

const GENRE_LABEL = {
  nollywood: "Nollywood",
  drama: "Drama",
  comedy: "Comedy",
  action: "Action",
  romance: "Romance",
  thriller: "Thriller",
  epic: "Epic",
  documentary: "Documentary",
  kids: "Kids",
  music: "Music",
  other: "Other",
};

const Movies = () => {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeGenre = searchParams.get("genre") || null;
  const [items, setItems] = useState([]);
  const [genres, setGenres] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const q = new URLSearchParams({ sort: "featured", limit: "40" });
    if (activeGenre) q.set("genre", activeGenre);
    api
      .get(`/movies?${q.toString()}`)
      .then((r) => {
        setItems(r.data?.items || []);
        setGenres(r.data?.genres || []);
      })
      .catch(() => toast.error("Could not load movies"))
      .finally(() => setLoading(false));
  }, [activeGenre]);

  const setGenre = (g) => {
    if (!g) searchParams.delete("genre");
    else searchParams.set("genre", g);
    setSearchParams(searchParams, { replace: true });
  };

  return (
    <div className="min-h-screen bg-[#0B0A0F] text-white" data-testid="movies-page">
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-white/10">
        <div
          className="absolute inset-0 pointer-events-none opacity-95"
          style={{
            background:
              "radial-gradient(circle at 85% 0%, #E4B851 0%, transparent 42%), radial-gradient(circle at 0% 100%, #B01E28 0%, transparent 45%), linear-gradient(140deg, #0B0A0F 0%, #17121b 100%)",
          }}
        />
        <div className="relative max-w-6xl mx-auto px-5 py-16 sm:py-24">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 border border-white/15 text-[11px] uppercase tracking-[0.35em] font-semibold text-[#E4B851] mb-5">
            <Clapperboard className="w-3 h-3" /> African Cinema
          </div>
          <h1 className="font-display text-4xl sm:text-6xl tracking-tight max-w-3xl leading-[1.05]">
            The home of{" "}
            <span
              className="bg-clip-text text-transparent"
              style={{ backgroundImage: "linear-gradient(120deg, #E4B851 0%, #F26D5B 55%, #B01E28 100%)" }}
            >
              Nollywood
            </span>{" "}
            trailers &amp; premieres.
          </h1>
          <p className="text-base text-white/70 max-w-2xl mt-5 leading-relaxed">
            Watch trailers for the latest African films, discover independent
            filmmakers, and click through to stream, buy or rent the full movie.
            Promoting a film? Put your trailer in front of the diaspora.
          </p>
          <div className="mt-7 flex flex-wrap gap-3">
            {user ? (
              <Link
                to="/movies/new"
                data-testid="movies-create-btn"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-semibold text-[#0B0A0F] text-sm"
                style={{ background: "linear-gradient(120deg, #E4B851 0%, #F26D5B 100%)" }}
              >
                <Plus className="w-4 h-4" /> Submit your movie
              </Link>
            ) : (
              <Link
                to={`/login?next=${encodeURIComponent("/movies/new")}`}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-semibold text-[#0B0A0F] text-sm bg-white"
              >
                Sign in to submit a film
              </Link>
            )}
            <a
              href="#movie-grid"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-white/25 text-white text-sm hover:bg-white/10"
            >
              Browse trailers
            </a>
          </div>
        </div>
      </section>

      {/* Genre filter */}
      <div className="max-w-6xl mx-auto px-5 mt-8" id="movie-grid">
        <div className="flex gap-2 flex-wrap" data-testid="movies-genre-filter">
          <button
            onClick={() => setGenre(null)}
            data-testid="movies-genre-all"
            className={`px-4 py-2 rounded-full text-sm font-semibold border transition-colors ${
              !activeGenre
                ? "bg-white text-[#0B0A0F] border-white"
                : "bg-white/5 text-white/80 border-white/15 hover:border-white/40"
            }`}
          >
            All genres
          </button>
          {genres.map((g) => {
            const active = activeGenre === g;
            return (
              <button
                key={g}
                onClick={() => setGenre(g)}
                data-testid={`movies-genre-${g}`}
                className={`px-4 py-2 rounded-full text-sm font-semibold border transition-colors ${
                  active
                    ? "text-[#0B0A0F] border-transparent bg-[#E4B851]"
                    : "bg-white/5 text-white/80 border-white/15 hover:border-white/40"
                }`}
              >
                {GENRE_LABEL[g] || g}
              </button>
            );
          })}
        </div>
      </div>

      {/* Grid */}
      <div className="max-w-6xl mx-auto px-5 pb-24 mt-8">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-6 h-6 animate-spin text-white/40" />
          </div>
        ) : items.length === 0 ? (
          <div
            className="text-center py-16 border-2 border-dashed border-white/15 rounded-3xl bg-white/[0.03]"
            data-testid="movies-empty"
          >
            <Clapperboard className="w-12 h-12 mx-auto text-white/25 mb-3" />
            <div className="font-semibold text-white/90">No trailers here yet</div>
            <div className="text-sm text-white/50 mt-1">
              Be the first to premiere a film on AfriMarket.
            </div>
            {user && (
              <Link
                to="/movies/new"
                className="mt-5 inline-block px-5 py-2 rounded-full bg-[#E4B851] text-[#0B0A0F] text-sm font-bold"
              >
                Submit your movie
              </Link>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
            {items.map((m) => (
              <Link
                key={m.movie_id}
                to={`/movies/${m.movie_id}`}
                data-testid={`movie-card-${m.movie_id}`}
                className="group block rounded-xl overflow-hidden bg-white/[0.04] border border-white/10 hover:border-[#E4B851]/60 transition-colors"
              >
                <div className="relative aspect-[2/3] bg-white/5 overflow-hidden">
                  {m.poster_url ? (
                    <img
                      src={m.poster_url}
                      alt={m.title}
                      loading="lazy"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-white/20">
                      <Clapperboard className="w-10 h-10" />
                    </div>
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
                  <div className="absolute top-2 left-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-black/60 backdrop-blur text-[10px] font-bold uppercase tracking-wider text-[#E4B851]">
                    {GENRE_LABEL[m.genre] || m.genre}
                  </div>
                  {m.is_featured && (
                    <div className="absolute top-2 right-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#E4B851] text-[#0B0A0F] text-[10px] font-bold uppercase tracking-wider">
                      <Star className="w-2.5 h-2.5" /> Featured
                    </div>
                  )}
                  <div className="absolute bottom-2 left-2 right-2">
                    <div className="inline-flex items-center gap-1 text-[11px] text-white/70">
                      <Play className="w-3 h-3 fill-white/70" /> Watch trailer
                    </div>
                  </div>
                </div>
                <div className="p-3">
                  <div className="font-semibold text-sm text-white line-clamp-1 leading-snug">
                    {m.title}
                  </div>
                  <div className="text-[11px] text-white/50 mt-1 flex items-center gap-2">
                    <span>{m.country}</span>
                    {m.year ? <span>· {m.year}</span> : null}
                    <span className="inline-flex items-center gap-0.5 ml-auto">
                      <Eye className="w-3 h-3" /> {m.view_count}
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Movies;
